// @flow
import React, { Component } from 'react'
import { connect } from 'react-redux'
import _ from 'lodash'
import { getDataList } from '../../actions/earnings'
import { updateTargetsList, updateTarget } from '../../actions/targets'
import { TableSearch, TableInput, TableKey, TableDate } from './style'
import { Table, Button, Icon, Row, Input, Tooltip } from 'antd'
import { Link } from 'react-router-dom'
import moment from 'moment-timezone';
import {CSVLink} from 'react-csv';

type Props = {
  loadEarningsData: void => void,
  upcomingEarningsTargets: Object[],
  onUpdateTargets: string[] => void,
  onUpdateTarget: string => void,
  targets: string[],
  target: string,
  isFromDashBoard: boolean,
}

type State = {
  filterDropdownVisible: boolean,
  filterSymbolDropdownVisible: boolean,
  filterCompanyNameDropdownVisible: boolean,
  data: any,
  filteredData: Object[],
  searchText: string,
  searchTextSymbol: string,
  searchCompanyName: string,
  filtered: boolean,
  filteredSymbol: boolean,
  filteredCompanyName: boolean,
  selectedCheckbox: number[],
  allSymbols: string[]
}

class EarningsTable extends Component<Props, State> {

  state: State = {
    filterDropdownVisible: false,
    filterSymbolDropdownVisible: false,
    filterCompanyNameDropdownVisible: false,
    data: [],
    filteredData: [],
    searchText: '',
    searchTextSymbol: '',
    searchCompanyName: '',
    filtered: false,
    filteredSymbol: false,
    filteredCompanyName: false,
    selectedCheckbox: [],
    allSymbols: []
  }

  componentDidMount() {
    
    // get upcoming earnings
    const { 
      upcomingEarningsTargets,
      loadEarningsData
    } = this.props

    if (!upcomingEarningsTargets.length) loadEarningsData()
    else this.loadListData()
  }

  componentDidUpdate() {

    // prop and state
    const {
      targets,
      target,
      onUpdateTarget
    } = this.props
    const { 
      data, 
      allSymbols, 
      selectedCheckbox 
    } = this.state

    // validate that data is ready to be added to chart
    if (!data.length) this.loadListData()

    // update checked box
    let selected: number[] = []

    // reset to initial state on last check
    if (!targets.length && (selected.toString() !== selectedCheckbox.toString())) {

      this.setState({ selectedCheckbox: selected })
      
    } else {

      // loop to add each index on update
      targets.forEach((target: string, index: number) => {

        // save each index
        selected = [...selected, allSymbols.indexOf(target)]

        // update state if arrays are different
        if (index === targets.length - 1 && selected.toString() !== selectedCheckbox.toString()) {
          this.setState({ selectedCheckbox: selected })
        }
      })
    }

    // handle target
    if (!targets.includes(target)) onUpdateTarget(targets[0])
  }

  // --- Load Data ---
  loadListData() {

    // update data state
    const { upcomingEarningsTargets } = this.props
    let earningsData = upcomingEarningsTargets
    // arrays for state
    let data: Object[] = []
    let filteredData: Object[] = []
    let allSymbols: string[] = []

    if (this.props.isFromDashBoard) {
      const today = moment();
      earningsData = earningsData.filter(x => {
        const date = moment(x.earningsDate);
        const diff = date.diff(today, 'days')
        return diff < 2 && diff > -1;
      });
    }

    // fill arrays
    earningsData.forEach((target: Object, i: number) => {
      let daysTill: number = moment(target.earningsDate).diff(moment().tz('America/New_York').format("YYYY-MM-DD"), 'days')

      // trade type
      let tradeType: string = ''
      tradeType = daysTill <= 5 ? tradeType = 'Earnings' : tradeType
      tradeType = daysTill >= 20 && daysTill <= 35 ? tradeType = 'Pre Earnings' : tradeType

      if(daysTill === 0){
        daysTill = 'Today'
        if(target.releaseToD === 'BMO'){
          daysTill = 'Today Pre Market'
        }else if(target.releaseToD === 'AMC'){
          daysTill = 'Today After Close'
        }
      } else if(daysTill === 1) {
        daysTill = 'Tomorrow'
        if(target.releaseToD === 'BMO'){
          daysTill = 'Tomorrow Pre Market'
        }else if(target.releaseToD === 'AMC'){
          daysTill = 'Tomorrow After Close'
        }
      } else {
        daysTill = `${daysTill} days`
      }
      // updated data object
      const newData: Object = {
        key: i,
        symbol: target.symbol,
        companyName: target.name,
        status: target.confirmed === 'Confirmed' ? 'Confirmed' : 'Unconfirmed',
        earningsDate: target.earningsDate,
        earningsDateFull: target.earningsDate,
        daysTill: daysTill,
        tod: target.releaseToD || 'AMC',
        earningStatus: '',
        tradeStyle: tradeType,
        symbol2: target.symbol,
      }

      // update arrays
      data = [...data, newData]
      filteredData = [...filteredData, newData]
      allSymbols = [...allSymbols, newData.symbol]

      // update state
      if (i === earningsData.length - 1) {

        this.setState({
          data,
          filteredData,
          allSymbols
        })
      }
    })
  }


  // --- Symbol ---
  onInputChangeSymbol = (e: Object) => {

    this.setState({ searchTextSymbol: e.target.value })
  }

  onSearchSymbol = () => {

    // Searched for value
    const { searchTextSymbol } = this.state
    const reg: RegExp = new RegExp(searchTextSymbol, 'gi')

    // update state of symbol list
    this.setState({
      filterSymbolDropdownVisible: false,
      filteredSymbol: !!searchTextSymbol,
      filteredData: this.state.data.map((record: Object): null | State => {
        const match: string[] = record.symbol.match(reg)
        if (!match) return null
        return {
          ...record,
          symbol: (
            <span>
              {
                record.symbol.split(reg).map((text: string, i: number): TableKey[] | string => (
                  i > 0 ? [<TableKey key={i}>{match[0]}</TableKey>, text] : text
                ))
              }
            </span>
          )
        }
      }).filter((record: Object) => !!record)
    })
  }


  // --- Company Name ---
  onCompanyNameChange = (e: Object) => {

    this.setState({ searchCompanyName: e.target.value })
  }

  onCompanyNameSearch = () => {

    // searched for value
    const { searchCompanyName } = this.state
    const reg: RegExp = new RegExp(searchCompanyName, 'gi')

    // update state of company name list
    this.setState({
      filterCompanyNameDropdownVisible: false,
      filteredCompanyName: !!searchCompanyName,
      filteredData: this.state.data.map((record: Object): null | State => {
        const match: string[] = record.companyName.match(reg)
        if (!match) return null
        return {
          ...record,
          companyName: (
            <span>
              {
                record.companyName.split(reg).map((text: string, i: number): TableKey[] | string => (
                  i > 0 ? [<TableKey key={i}>{match[0]}</TableKey>, text] : text
                ))
              }
            </span>
          )
        }
      }).filter((record: Object) => !!record)
    })
  }

  getExportData = (data) => {
    const newData = _.cloneDeep(data)
    return newData.map(x=>{
      x.symbol = x.symbol2
      delete x.symbol2;
      return x;
    })
  }

  // --- Input Elements ---
  searchInput: any
  searchSymbolInput: any
  searchCompanyNameInput: any

  render() {

    // --- Select Box ---
    const rowSelection = {
      selectedRowKeys: this.state.selectedCheckbox,
      columnWidth: 30,
      onChange: (selectedCheckbox: number[], selectedRows: Object[]) => {

        // Set selected rows
        const symbols: string[] = selectedRows.map((x: Object): string => x.symbol)

        this.props.onUpdateTargets(symbols)
        this.setState({ selectedCheckbox })
      }
    }

    // --- Columns ---
    const columns = [{
      width: 92,
      title: 'Days',
      dataIndex: 'daysTill',
      key: 'daysTill',
      defaultSortOrder: 'ascend'
    }, {
      width: 60,
      title: 'Type',
      dataIndex: 'tradeStyle',
      key: 'tradeStyle',
      filters: [{
        text: 'Earnings',
        value: 'Earnings'
      }, {
        text: 'Pre Earnings',
        value: 'Pre Earnings'
      }],
      onFilter: (value: string, record: Object) => record.tradeStyle.indexOf(value) === 0,
    }, {
      width: 60,
      title: 'Symbol',
      dataIndex: 'symbol',
      key: 'symbol',
      render: (text, record) => <Link to={`/stocks/${record.symbol2}`}>{text}</Link>,
      filterDropdown: (

        // --- Search By Symbol ---
        <TableSearch>
          <Input
            ref={ele => { this.searchSymbolInput = ele }}
            placeholder='Symbol'
            value={this.state.searchTextSymbol}
            onChange={this.onInputChangeSymbol}
            onPressEnter={this.onSearchSymbol}
            style={TableInput}
          />
          <Button type='primary' onClick={this.onSearchSymbol}>Search</Button>
        </TableSearch>
      ),
      filterIcon: <Icon type='search' style={{ color: this.state.filteredSymbol ? '#108ee9' : '#aaa' }} />,
      filterSymbolDropdownVisible: this.state.filterSymbolDropdownVisible,
      onFilterDropdownVisibleChange: (visible: boolean) => {

        this.setState({
          filterSymbolDropdownVisible: visible
        }, () => this.searchSymbolInput && this.searchSymbolInput.focus())
      }
    }, {
      width: 150,
      title: 'Name',
      dataIndex: 'companyName',
      key: 'companyName',
      filterDropdown: (

        // --- Search by Company Name ---
        <TableSearch>
          <Input
            ref={ele => { this.searchCompanyNameInput = ele }}
            placeholder='Company'
            value={this.state.searchCompanyName}
            onChange={this.onCompanyNameChange}
            onPressEnter={this.onCompanyNameSearch}
            style={TableInput}
          />

          <Button type='primary' onClick={this.onCompanyNameSearch}>Search</Button>
        </TableSearch>
      ),
      filterIcon: <Icon type='search' style={{ color: this.state.filteredCompanyName ? '#108ee9' : '#aaa' }} />,
      filterCompanyNameDropdownVisible: this.state.filterCompanyNameDropdownVisible,
      onFilterDropdownVisibleChange: (visible: boolean) => {

        this.setState({
          filterCompanyNameDropdownVisible: visible
        }, () => this.searchCompanyNameInput && this.searchCompanyNameInput.focus())
      }
    }, {
      width: 100,
      title: 'Date',
      dataIndex: 'earningStatus',
      key: 'earningStatus',
      sorter: (a: Object, b: Object): number => +moment(a.earningsDateFull) - +moment(b.earningsDateFull),
      render: (text: string, record: Object, index: number): Row => {
        // Combine earnings date, confirmed/unconfirmed and time of day
        const { 
          earningsDate, 
          status, 
        } = record
        return (
          <Row>
            <TableDate>
              <span>
                 <Tooltip placement="top" title={status === 'Confirmed' ? 'Confirmed' : 'Estimated'}>
                  <Icon type={status === 'Confirmed' ? 'check' : 'question'} />&nbsp;&nbsp;
                </Tooltip>
                {earningsDate}
              </span>
            </TableDate>
          </Row>
        )
      }
    }, {
      width: 100,
      title: 'Time of Day',
      dataIndex: 'tod',
      key: 'tod',
      render: (text: string): Row => {
        return (
            <Tooltip placement="top" title={text === 'AMC' ? 'After Market Close' : 'Before Market Open'}>
              <strong>{text}</strong>
            </Tooltip>
        )
      }
    }]

    // --- Table ---
    return (
      <section>
        <TableKey>
          {!!this.state.filteredData.length &&
          <TableSearch>
            <CSVLink data={this.state.filteredData && this.getExportData(this.state.filteredData)}>
              <Button type="primary" icon="download">Download as CSV</Button>
            </CSVLink>
            <br/>
            <Table
              rowSelection={rowSelection}
              columns={columns}
              size={'small'}
              dataSource={this.state.filteredData}
              pagination={false}/>
          </TableSearch>
          }
        </TableKey>
      </section>
    )
  }
}

const mapStateToProps = (state: Object) => ({
  upcomingEarningsTargets: state.earnings.upcomingEarningsTargets,
  targets: state.targets.targets,
  target: state.targets.target
})

const mapDispatchToProps = (dispatch: Function) => ({
  loadEarningsData() {
    getDataList(dispatch)
  },
  onUpdateTargets(targetList: string[]) {
    dispatch(updateTargetsList(targetList))
  },
  onUpdateTarget(target: string) {
    dispatch(updateTarget(target))
  }
})

export default connect(mapStateToProps, mapDispatchToProps)(EarningsTable)



// WEBPACK FOOTER //
// ./src/components/table-earnings/index.js